// The canvas's copy of the gate: a MIRROR of `service/bi/core/validate_built.py`,
// with the same rules in the same order. The server stays the gate. This only
// moves a refusal to the moment a field is picked. It must fail safe: report
// issues, never rewrite a chart, never allow what the server refuses. Each rule
// is pinned in `built-chart.test.ts`. See frontend/bi/CLAUDE.md ("MANNERS,
// never the gate").
import type {
  BiSource,
  BiSourceColumn,
  ChartQuery,
  ChartSpec,
  MetricSpec,
  SimpleAggregate,
  SimpleMetric,
} from "@bi/shared/types";

/** Mirrors `validate_built._VIZ_TYPES`. Duplicated from the generated union on
 *  purpose and pinned by test, exactly as the backend duplicates it from its
 *  own Literal: a set derived by cleverness from a re-exported type is a set
 *  that can silently become empty, and an empty allowlist fails OPEN. */
export const BUILT_VIZ_TYPES = [
  "echarts_timeseries_bar",
  "echarts_timeseries_line",
  "table",
  "big_number_total",
  "big_number",
  "pie",
  "gauge_chart",
  "funnel",
] as const;

export type BuiltVizType = (typeof BUILT_VIZ_TYPES)[number];

/** `Settings.bi_built_row_limit_max` / `bi_built_series_limit_max` defaults.
 *
 *  The ENVIRONMENT's ceilings, which the client cannot read — these are the
 *  shipped defaults, so a deployment that lowered one refuses server-side with
 *  a message naming the real number. The canvas under-offering is the right way
 *  round: it never lets somebody compose past the default and discover the real
 *  limit only on save. */
export const BUILT_ROW_LIMIT_MAX = 5000;
export const BUILT_SERIES_LIMIT_MAX = 50;

export interface BuiltLimits {
  rowLimitMax: number;
  seriesLimitMax: number;
}

export const DEFAULT_BUILT_LIMITS: BuiltLimits = {
  rowLimitMax: BUILT_ROW_LIMIT_MAX,
  seriesLimitMax: BUILT_SERIES_LIMIT_MAX,
};

const TYPE_ALIASES: Record<string, string> = {
  INT: "INT64",
  SMALLINT: "INT64",
  INTEGER: "INT64",
  BIGINT: "INT64",
  TINYINT: "INT64",
  BYTEINT: "INT64",
  DECIMAL: "NUMERIC",
  BIGDECIMAL: "BIGNUMERIC",
  FLOAT: "FLOAT64",
  DOUBLE: "FLOAT64",
  BOOLEAN: "BOOL",
  RECORD: "STRUCT",
};

const NUMERIC_TYPES = new Set(["INT64", "FLOAT64", "NUMERIC", "BIGNUMERIC"]);

/** Mirrors `registry.canonical_bq_type`: normalizes a SPELLING, never an
 *  allowlist. An unknown name comes back upper-cased and unchanged. */
export function canonicalBqType(bqType: string): string {
  const upper = bqType.trim().toUpperCase();
  return TYPE_ALIASES[upper] ?? upper;
}

const EQUALITY_AGGREGATES: SimpleAggregate[] = ["count", "count_distinct", "min", "max"];
const NUMERIC_AGGREGATES: SimpleAggregate[] = [
  "sum",
  "count",
  "count_distinct",
  "avg",
  "min",
  "max",
];

/** Which aggregates this column's type can carry — mirrors
 *  `registry.aggregations_for_column`, in the `SimpleMetric.aggregate` spelling
 *  the canvas actually stores (lower case) rather than the compiler's SQL
 *  spelling. The property panel offers exactly this list, which is why picking
 *  an illegal aggregate is normally impossible rather than merely refused. */
export function aggregatesForType(bqType: string): SimpleAggregate[] {
  return NUMERIC_TYPES.has(canonicalBqType(bqType))
    ? [...NUMERIC_AGGREGATES]
    : [...EQUALITY_AGGREGATES];
}

export function aggregatesForColumn(column: BiSourceColumn): SimpleAggregate[] {
  return aggregatesForType(column.bq_type);
}

export function isSimpleMetric(metric: MetricSpec): metric is SimpleMetric {
  return metric.kind === "simple";
}

/** One refusal, in the shape the property panel renders: which field is wrong,
 *  and what to do about it. `field` is a stable key, not prose, so the panel can
 *  highlight the control that owns it. */
export interface BuiltChartIssue {
  field: string;
  message: string;
}

function metricLabels(query: ChartQuery): Set<string> {
  const labels = new Set<string>();
  for (const m of query.metrics ?? []) labels.add(m.label);
  if (query.metric) labels.add(query.metric.label);
  return labels;
}

function checkMetric(
  metric: MetricSpec,
  source: BiSource,
  where: string,
  issues: BuiltChartIssue[],
  unresolved: [string, string][],
): void {
  if (!isSimpleMetric(metric)) {
    issues.push({
      field: where,
      message: `${metric.label} is a raw SQL metric. A built chart may only carry a column plus an aggregate.`,
    });
    return;
  }
  if (metric.column === null || metric.column === undefined) return; // COUNT(*)
  const column = source.columns?.find((c) => c.name === metric.column);
  if (!column) {
    unresolved.push([`${where} ${metric.label}`, metric.column]);
    return;
  }
  if (!aggregatesForColumn(column).includes(metric.aggregate)) {
    issues.push({
      field: where,
      message: `${metric.label}: ${metric.aggregate} is not allowed on ${column.name} (type ${canonicalBqType(column.bq_type)}). Allowed: ${aggregatesForColumn(column).join(", ")}.`,
    });
  }
}

/**
 * Every reason `validate_built_chart` would refuse this chart, or an empty
 * list.
 *
 * A LIST rather than the first failure, and that is the one deliberate
 * difference from the server: the server admits a chart whole or refuses it
 * whole and names the first offending field, because it is answering a request.
 * This is answering a person who is composing, and showing them one problem at
 * a time when there are three turns a fix into a guessing game.
 *
 * `source` may be `undefined` while the source detail is still loading. That is
 * not a violation and reports nothing: an editor that flashes "unknown column"
 * across every field for the first second of a page load has taught its reader
 * to ignore it.
 */
export function validateBuiltChart(
  chart: ChartSpec,
  source: BiSource | null | undefined,
  limits: BuiltLimits = DEFAULT_BUILT_LIMITS,
): BuiltChartIssue[] {
  const issues: BuiltChartIssue[] = [];
  if (chart.source !== "built") {
    issues.push({ field: "source", message: "This chart is not a built chart." });
    return issues;
  }
  if (!chart.source_id) {
    issues.push({ field: "source", message: "Pick a data source for this tile." });
    return issues;
  }
  if (!source) return issues; // still loading — see the docstring
  if (chart.source_id !== source.id) {
    issues.push({
      field: "source",
      message: `This chart names source ${chart.source_id}, but is being checked against ${source.id}.`,
    });
    return issues;
  }
  if (!(BUILT_VIZ_TYPES as readonly string[]).includes(chart.viz_type)) {
    issues.push({ field: "viz_type", message: `${chart.viz_type} is not a chart type you can build.` });
  }
  if (chart.extras && Object.keys(chart.extras).length > 0) {
    issues.push({ field: "extras", message: "A built chart carries no unmodelled keys." });
  }

  const query = chart.query;
  if (query.extras && Object.keys(query.extras).length > 0) {
    issues.push({ field: "query.extras", message: "A built query carries no unmodelled keys." });
  }

  const unresolved: [string, string][] = [];
  if (query.metric) checkMetric(query.metric, source, "metric", issues, unresolved);
  for (const metric of query.metrics ?? []) {
    checkMetric(metric, source, "metrics", issues, unresolved);
  }

  (query.filters ?? []).forEach((filter, i) => {
    if (filter.kind !== "simple") {
      issues.push({
        field: `filters.${i}`,
        message: "A built chart filters by column, operator and a value only.",
      });
      return;
    }
    unresolved.push([`filter ${i + 1}`, filter.subject]);
  });

  for (const name of query.groupby ?? []) unresolved.push(["breakdown", name]);
  for (const name of query.all_columns ?? []) unresolved.push(["columns", name]);
  const labels = metricLabels(query);
  for (const order of query.order_by ?? []) {
    // An ORDER BY may legitimately name a metric LABEL rather than a column —
    // the compiler translates it to the metric's alias, which is the ordinary
    // "sort by the measure" every bar chart produces.
    if (!labels.has(order.column)) unresolved.push(["sort", order.column]);
  }
  if (query.x_axis) unresolved.push(["dimension", query.x_axis]);
  if (query.time?.granularity_sqla) unresolved.push(["time column", query.time.granularity_sqla]);

  if (chart.display && "annotations" in chart.display && chart.display.annotations) {
    const annotations = chart.display.annotations;
    if (!annotations.source_id) {
      issues.push({ field: "annotations", message: "Pick the events source for the marks." });
    }
  }

  const declared = new Set((source.columns ?? []).map((c) => c.name));
  for (const [field, name] of unresolved) {
    if (!declared.has(name)) {
      issues.push({
        field,
        message: `${field} names ${name}, which ${source.id} does not have.`,
      });
    }
  }

  if (query.row_limit != null && query.row_limit > limits.rowLimitMax) {
    issues.push({
      field: "row_limit",
      message: `Row limit ${query.row_limit} is over this environment's ceiling of ${limits.rowLimitMax}.`,
    });
  }
  if (query.series_limit != null && query.series_limit > limits.seriesLimitMax) {
    issues.push({
      field: "series_limit",
      message: `Series limit ${query.series_limit} is over this environment's ceiling of ${limits.seriesLimitMax}.`,
    });
  }
  return issues;
}

/** Whether a chart is composed enough to draw anything at all.
 *
 *  Separate from `validateBuiltChart` because "not finished yet" is not the
 *  same as "not allowed": a scorecard with no measure picked is the ordinary
 *  state of a tile one second after it is created, and reporting it as a
 *  violation would fill the panel with red before the person has done anything
 *  wrong. This is what gates the PREVIEW request instead — there is no point
 *  spending a query on a chart that names no measure and no column. */
export function isChartComposed(chart: ChartSpec): boolean {
  const q = chart.query;
  const hasMetric = Boolean(q.metric) || (q.metrics?.length ?? 0) > 0;
  const hasColumns = (q.all_columns?.length ?? 0) > 0;
  if (q.query_mode === "raw") return hasColumns;
  return hasMetric;
}
